// Section progress for parallax and other scroll-linked effects.
//
// Writes P (0 → 1) to a custom property on the element: 0 when its top enters
// from below, 1 when its bottom leaves at the top.
//
//   useSectionProgress(ref);                          // writes --ld-p
//   useSectionProgress(ref, { smooth: true });        // eases with a .133s lag
//   useSectionProgress(ref, { onFrame: (p) => ... }); // also calls back
//
// CSS: translate: 0 calc((var(--ld-p, .5) - .5) * -4rem);
//
// Runs only at 1024 and up without reduced motion, and pauses when the element
// is more than 120px off screen. When off, the property is removed, so write
// the fallback that means "no movement" (.5 for a drift).
//
// Pages/Home/motion/useSceneProgress has the same formula but no smoothing, no
// width gate and no callback, and it finds its elements once per page.

import { useEffect, useRef } from "react";
import { clamp01, DESKTOP, onScrollFrame, prefersReducedMotion } from "./env";

const TIME_CONSTANT = 0.133;

export default function useSectionProgress(ref, { property = "--ld-p", smooth = false, query = DESKTOP, onFrame } = {}) {
    const callback = useRef(onFrame);
    useEffect(() => { callback.current = onFrame; });

    useEffect(() => {
        const el = ref?.current;
        if (!el) return undefined;

        const mq = window.matchMedia?.(query);
        let stop = () => {};

        const start = () => {
            if (prefersReducedMotion()) return () => {};

            let visible = false;
            let current = null;
            let target = 0;
            let raf = 0;
            let last = 0;

            const write = (p) => {
                el.style.setProperty(property, p.toFixed(4));
                callback.current?.(p);
            };

            const ease = (now) => {
                raf = 0;
                const dt = Math.min((now - last) / 1000, 0.1);
                last = now;
                current += (target - current) * (1 - Math.exp(-dt / TIME_CONSTANT));
                if (Math.abs(target - current) < 0.0005) current = target;
                write(current);
                if (current !== target && visible) raf = requestAnimationFrame(ease);
            };

            const measure = (force) => {
                if (!visible && force !== true) return;
                const r = el.getBoundingClientRect();
                const vh = window.innerHeight || 1;
                target = clamp01((vh - r.top) / (vh + r.height));
                if (!smooth || current === null) {
                    current = target;
                    write(current);
                    return;
                }
                if (!raf) {
                    last = performance.now();
                    raf = requestAnimationFrame(ease);
                }
            };

            // BODY scrolls, so a rootMargin alone does not reach past its edge;
            // scrollMargin does, where the browser has it. The first measure
            // gives an off-screen element its value, so nothing jumps as it enters.
            measure(true);
            const io = new IntersectionObserver((entries) => {
                visible = entries[entries.length - 1].isIntersecting;
                measure();
            }, { rootMargin: "120px 0px", scrollMargin: "120px 0px" });
            io.observe(el);
            const off = onScrollFrame(measure);

            return () => {
                cancelAnimationFrame(raf);
                io.disconnect();
                off();
                el.style.removeProperty(property);
            };
        };

        const apply = () => {
            stop();
            stop = !mq || mq.matches ? start() : () => {};
        };
        apply();
        mq?.addEventListener("change", apply);

        return () => {
            mq?.removeEventListener("change", apply);
            stop();
        };
    }, [ref, property, smooth, query]);
}
